import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getUserModules, normalizeUserModules, UserModules } from '../services/ModuleService';
import SessionService from '../services/SessionService';

interface ModuleContextType {
  modules: UserModules;
  loading: boolean;
  hasModule: (key: keyof UserModules) => boolean;
  refreshModules: () => Promise<void>;
}

const defaultModules: UserModules = {
  email: true,
  campaign: true,
  crm: true,
  schedule: true,
  calendar: true,
  aiDocuments: false,
};

const ModuleContext = createContext<ModuleContextType>({
  modules: defaultModules,
  loading: true,
  hasModule: () => true,
  refreshModules: async () => {},
});

export const useModules = () => useContext(ModuleContext);

export const ModuleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modules, setModules] = useState<UserModules>(defaultModules);
  const [loading, setLoading] = useState(true);
  const lastBackgroundRefreshAt = useRef(0);

  const applyModules = useCallback((data: UserModules) => {
    setModules(data);
    setLoading(false);
  }, []);

  const loadModules = useCallback(async (forceRefresh = false) => {
    if (!SessionService.isAuthenticated()) {
      setModules(defaultModules);
      setLoading(false);
      return;
    }

    const data = await getUserModules(forceRefresh);
    applyModules(data);
  }, [applyModules]);

  useEffect(() => {
    void (async () => {
      await loadModules(false);
      await loadModules(true);
    })();
  }, [loadModules]);

  const refreshModules = useCallback(async () => {
    if (!SessionService.isAuthenticated()) return;
    const data = await getUserModules(true);
    applyModules(data);
  }, [applyModules]);

  useEffect(() => {
    const onModulesChanged = (event: Event) => {
      const detail = (event as CustomEvent<UserModules>).detail;
      if (detail) applyModules(normalizeUserModules(detail));
    };

    const refreshInBackground = () => {
      if (!SessionService.isAuthenticated()) return;
      const now = Date.now();
      if (now - lastBackgroundRefreshAt.current < 60_000) return;
      lastBackgroundRefreshAt.current = now;
      void getUserModules(true).then(data => applyModules(data));
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshInBackground();
    };

    window.addEventListener('focus', refreshInBackground);
    window.addEventListener('modulesChanged', onModulesChanged);
    document.addEventListener('visibilitychange', onVisibility);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshInBackground();
    }, 5 * 60_000);

    return () => {
      window.removeEventListener('focus', refreshInBackground);
      window.removeEventListener('modulesChanged', onModulesChanged);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [applyModules]);

  const hasModule = useCallback(
    (key: keyof UserModules) => modules[key],
    [modules]
  );

  return (
    <ModuleContext.Provider value={{ modules, loading, hasModule, refreshModules }}>
      {children}
    </ModuleContext.Provider>
  );
};

export default ModuleContext;
